import {
  type EComDataType,
  type ModifyCartItemByDecoratedProduct
} from '../model';
import { isDefined } from '~/utils/guards/isDefined';
import {
  type MoneyFragment,
  type ShippingCartAddressFragment
} from '~/lib/Shop/generated/schema';

/**
 * Creates a single entry inside an analytics event.
 *
 * @see https://gcp.baslerweb.com/jira/browse/WEB2-1695
 *
 * @param decoratedProduct The decorated product to use.
 * @param quantity The quantity to use.
 */
async function transformItem({
  decoratedProduct,
  quantity
}: ModifyCartItemByDecoratedProduct) {
  const breadcrumbs = await decoratedProduct.getBreadcrumbs();
  return {
    item_id: decoratedProduct.sku, // sku_107822
    item_name: decoratedProduct.modelName, // a2A1920-51gmBAS - Basler ace 2
    item_category: breadcrumbs?.[0]?.text ?? decoratedProduct.category, // decoratedProduct.category Area scan camera
    item_category_id: decoratedProduct.categoryId, // Area scan camera
    item_category2: breadcrumbs?.[1]?.text ?? '',
    item_category3: breadcrumbs?.[2]?.text ?? '',
    item_category4: breadcrumbs?.[3]?.text ?? '',
    item_category5: breadcrumbs?.[4]?.text ?? '',
    item_brand: decoratedProduct.brandData?.[0]?.label?.label ?? '',
    price: decoratedProduct.price, // 15.5
    quantity, // 1 -- always 1 in case of view_item
    delivery_time:
      decoratedProduct.showShippingTime &&
      isDefined(decoratedProduct.shippingTimeText)
        ? decoratedProduct.shippingTimeText // 4-5 weeks
        : 'Not available',
    fastdelivery_available: decoratedProduct.hasFastDelivery,
    product_type: decoratedProduct.getAttributeSetName(),
    discount: decoratedProduct.discount
  };
}

export async function createEcommerceTrackingData({
  cartItems,
  source = 'website',
  tax,
  transactionId,
  coupon,
  shipping,
  paymentType,
  checkoutStep,
  cartId
}: {
  cartItems: ReadonlyArray<ModifyCartItemByDecoratedProduct>;
  source?: string;
  tax?: MoneyFragment;
  transactionId?: string;
  coupon?: string;
  shipping?: ShippingCartAddressFragment;
  paymentType?: string;
  checkoutStep?: string;
  cartId?: string;
}) {
  const [firstItem] = cartItems;
  if (!firstItem) {
    return;
  }

  const eComData: EComDataType = {
    currency: firstItem.decoratedProduct.currencySign, // $
    value: cartItems.reduce((collector, { decoratedProduct, quantity }) => {
      const price = Number(decoratedProduct.price);
      if (quantity <= 0 || isNaN(price)) {
        return collector;
      }

      return collector + quantity * price;
    }, 0), // 15.5
    items: await Promise.all(cartItems.map(transformItem)),
    ecom_source: source // @see https://gcp.baslerweb.com/jira/browse/WEB2-1724
  };

  if (tax) {
    eComData.tax = tax;
  }

  if (transactionId) {
    eComData.transaction_id = transactionId;
  }

  if (checkoutStep) {
    eComData.checkout_step = checkoutStep;
  }

  if (coupon) {
    eComData.coupon = coupon;
  }

  if (shipping) {
    eComData.shipping = shipping;
  }

  if (paymentType) {
    eComData.payment_type = paymentType;
  }

  if (cartId) {
    eComData.cart_id = cartId;
  }

  return eComData;
}
